import { useEffect, useState } from 'react';
import { LoaderCircle, Plus, X } from 'lucide-react';

import { ApiError } from '@/api/client';
import { useReceivePurchaseOrder } from '@/api/hooks/usePurchaseOrders';
import type { ReceiveBatchRequest, ReceiveLineRequest } from '@/api/purchasing';
import type { PurchaseOrder } from '@/api/types';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/use-toast';

interface BatchDraft {
  key: string;
  qty: string;
  batchNo: string;
  expiryDate: string;
  costPrice: string;
}

interface LineDraft {
  itemIndex: number;
  medicineName: string;
  outstanding: number;
  receivedQty: string;
  batchNo: string;
  expiryDate: string;
  costPrice: string;
  /** When true, `batches` replaces the single-batch fields above on submit. */
  multiBatch: boolean;
  batches: BatchDraft[];
}

function makeKey() {
  return `b${Date.now()}${Math.random().toString(36).slice(2, 7)}`;
}

export function ReceivePurchaseOrderDialog({
  po,
  open,
  onOpenChange,
}: {
  po: PurchaseOrder | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const receivePO = useReceivePurchaseOrder();
  const [lines, setLines] = useState<LineDraft[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && po) {
      setLines(
        po.items
          .map((item) => ({
            itemIndex: item.index,
            medicineName: item.medicineName ?? 'Unknown',
            outstanding: item.orderedQty - item.receivedQty,
            receivedQty: String(Math.max(0, item.orderedQty - item.receivedQty)),
            batchNo: item.batchNo ?? '',
            expiryDate: item.expiryDate?.split('T')[0] ?? '',
            costPrice: String(item.unitCost || ''),
            multiBatch: false,
            batches: [],
          }))
          .filter((line) => line.outstanding > 0),
      );
      setError(null);
    }
  }, [open, po]);

  if (!po) return null;

  function updateLine(itemIndex: number, patch: Partial<LineDraft>) {
    setLines((prev) => prev.map((l) => (l.itemIndex === itemIndex ? { ...l, ...patch } : l)));
  }

  function toggleMultiBatch(itemIndex: number) {
    setLines((prev) =>
      prev.map((l) => {
        if (l.itemIndex !== itemIndex) return l;
        if (!l.multiBatch && l.batches.length === 0) {
          // Seed one batch row from the current single-batch draft so
          // switching modes doesn't lose what was already typed.
          return {
            ...l,
            multiBatch: true,
            batches: [
              { key: makeKey(), qty: l.receivedQty, batchNo: l.batchNo, expiryDate: l.expiryDate, costPrice: l.costPrice },
            ],
          };
        }
        return { ...l, multiBatch: !l.multiBatch };
      }),
    );
  }

  function addBatch(itemIndex: number) {
    setLines((prev) =>
      prev.map((l) =>
        l.itemIndex === itemIndex
          ? { ...l, batches: [...l.batches, { key: makeKey(), qty: '', batchNo: '', expiryDate: '', costPrice: '' }] }
          : l,
      ),
    );
  }

  function removeBatch(itemIndex: number, key: string) {
    setLines((prev) =>
      prev.map((l) => (l.itemIndex === itemIndex ? { ...l, batches: l.batches.filter((b) => b.key !== key) } : l)),
    );
  }

  function updateBatch(itemIndex: number, key: string, patch: Partial<BatchDraft>) {
    setLines((prev) =>
      prev.map((l) =>
        l.itemIndex === itemIndex ? { ...l, batches: l.batches.map((b) => (b.key === key ? { ...b, ...patch } : b)) } : l,
      ),
    );
  }

  async function handleSubmit() {
    setError(null);

    const items: ReceiveLineRequest[] = lines
      .filter((line) => (line.multiBatch ? line.batches.some((b) => Number(b.qty) > 0) : Number(line.receivedQty) > 0))
      .map((line) => {
        if (line.multiBatch) {
          const batches: ReceiveBatchRequest[] = line.batches
            .filter((b) => Number(b.qty) > 0)
            .map((b) => ({
              qty: Number(b.qty),
              batchNo: b.batchNo.trim() || undefined,
              expiryDate: b.expiryDate || undefined,
              costPrice: b.costPrice ? Number(b.costPrice) : undefined,
            }));
          return { itemIndex: line.itemIndex, receivedQty: 0, batches };
        }
        return {
          itemIndex: line.itemIndex,
          receivedQty: Math.min(Number(line.receivedQty), line.outstanding),
          batchNo: line.batchNo.trim() || undefined,
          expiryDate: line.expiryDate || undefined,
          costPrice: line.costPrice ? Number(line.costPrice) : undefined,
        };
      });

    if (items.length === 0) {
      setError('Enter a received quantity for at least one item.');
      return;
    }

    try {
      await receivePO.mutateAsync({ id: po!.id, request: { items } });
      toast({ title: 'Receipt recorded', variant: 'success' });
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Please try again.');
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Receive — {po.poNumber}</DialogTitle>
        </DialogHeader>

        {lines.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            Everything on this order has already been received.
          </p>
        ) : (
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            {lines.map((line) => {
              const batchTotal = line.batches.reduce((sum, b) => sum + (Number(b.qty) || 0), 0);
              return (
                <div key={line.itemIndex} className="space-y-2 rounded-md border border-border p-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium">{line.medicineName}</p>
                    <p className="text-xs text-muted-foreground">Outstanding: {line.outstanding}</p>
                  </div>

                  {!line.multiBatch ? (
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-1">
                        <Label className="text-xs">Received qty</Label>
                        <Input
                          type="number"
                          min={0}
                          max={line.outstanding}
                          value={line.receivedQty}
                          onChange={(e) => updateLine(line.itemIndex, { receivedQty: e.target.value })}
                          className="h-8"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Batch no.</Label>
                        <Input
                          value={line.batchNo}
                          onChange={(e) => updateLine(line.itemIndex, { batchNo: e.target.value })}
                          className="h-8"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Expiry date</Label>
                        <Input
                          type="date"
                          value={line.expiryDate}
                          onChange={(e) => updateLine(line.itemIndex, { expiryDate: e.target.value })}
                          className="h-8"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Cost price (₹)</Label>
                        <Input
                          type="number"
                          min={0}
                          step="0.01"
                          value={line.costPrice}
                          onChange={(e) => updateLine(line.itemIndex, { costPrice: e.target.value })}
                          className="h-8"
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {line.batches.map((batch, i) => (
                        <div key={batch.key} className="space-y-2 rounded border border-border/70 bg-muted/30 p-2">
                          <div className="flex items-center justify-between">
                            <span className="text-[11px] font-medium text-muted-foreground">Batch {i + 1}</span>
                            <button
                              type="button"
                              onClick={() => removeBatch(line.itemIndex, batch.key)}
                              className="text-muted-foreground hover:text-foreground"
                              aria-label="Remove batch"
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div className="space-y-1">
                              <Label className="text-xs">Qty</Label>
                              <Input
                                type="number"
                                min={0}
                                value={batch.qty}
                                onChange={(e) => updateBatch(line.itemIndex, batch.key, { qty: e.target.value })}
                                className="h-8"
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Batch no.</Label>
                              <Input
                                value={batch.batchNo}
                                onChange={(e) => updateBatch(line.itemIndex, batch.key, { batchNo: e.target.value })}
                                className="h-8"
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Expiry date</Label>
                              <Input
                                type="date"
                                value={batch.expiryDate}
                                onChange={(e) => updateBatch(line.itemIndex, batch.key, { expiryDate: e.target.value })}
                                className="h-8"
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Cost price (₹)</Label>
                              <Input
                                type="number"
                                min={0}
                                step="0.01"
                                value={batch.costPrice}
                                onChange={(e) => updateBatch(line.itemIndex, batch.key, { costPrice: e.target.value })}
                                className="h-8"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                      <div className="flex items-center justify-between">
                        <button
                          type="button"
                          onClick={() => addBatch(line.itemIndex)}
                          className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                        >
                          <Plus className="h-3.5 w-3.5" /> Add batch
                        </button>
                        <span className="text-[11px] text-muted-foreground">
                          Total: {batchTotal} / {line.outstanding}
                        </span>
                      </div>
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={() => toggleMultiBatch(line.itemIndex)}
                    className="text-[11px] font-medium text-muted-foreground hover:text-foreground hover:underline"
                  >
                    {line.multiBatch ? 'Use a single batch' : 'Split into batches'}
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {error && (
          <p role="alert" className="rounded-md border border-status-expired/30 bg-status-expired/10 px-3 py-2 text-sm text-status-expired">
            {error}
          </p>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          {lines.length > 0 && (
            <Button type="button" onClick={handleSubmit} disabled={receivePO.isPending}>
              {receivePO.isPending && <LoaderCircle className="animate-spin" />}
              Record receipt
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
